/**
 * One wallet device's subscription to a ticket pass, stored at
 * `workspaces/{orgId}/events/{eventId}/walletRegistrations/{deviceId}__{serial}`.
 * Only the authenticated wallet web service and update fan-out use these
 * Admin SDK records; no client actions are declared.
 *
 * platform selects the push transport. Apple records carry APNs tokens;
 * Android records remain available for polling without being sent to APNs.
 * deviceId and passTypeId select polling subscriptions, serial selects the
 * ticket's devices, and updatedAt supplies the passesUpdatedSince tag.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

export const WalletRegistrationSchema = z.object({
	deviceId: z.string().min(1),
	// Existing registrations were written for Apple APNs; Android clients
	// identify themselves explicitly so their tokens never reach APNs.
	platform: z.enum(["apple", "android"]).default("apple"),
	passTypeId: z.string().min(1),
	serial: z.string().min(1),
	pushToken: z.string().min(1),
	updatedAt: z.custom<Timestamp>(),
});
export type WalletRegistration = z.infer<typeof WalletRegistrationSchema>;

export const WalletRegistration = entity<Actor>()({
	schema: WalletRegistrationSchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/walletRegistrations/{registrationId}",
		}),
	);
